import type { Event } from '../types/api';
import {
  getEventFormat,
  getTimeOfDay,
  getVibes,
  type EventFormat,
} from './eventPresentation';
import { compareEventLocalDates, localCalendarDateOffset } from './eventDateTime';

/**
 * Client-side filter model for event lists.
 *
 * Filters live in the URL so a filtered view is shareable and survives a
 * refresh, and a single `applyEventFilters` call feeds the list, the map, and
 * the exports so the three can never disagree.
 */

export type RelationshipFilter = 'HOSTING' | 'SPONSORING' | 'ATTENDING';
export type TimeOfDayFilter = 'Morning' | 'Afternoon' | 'Evening';
export type UpcomingHorizonDays = 30 | 90 | 180;

export const DEFAULT_UPCOMING_HORIZON_DAYS: UpcomingHorizonDays = 30;
export const UPCOMING_HORIZON_OPTIONS: UpcomingHorizonDays[] = [30, 90, 180];

export interface EventFilterState {
  horizonDays: UpcomingHorizonDays;
  dateFrom: string;
  dateTo: string;
  formats: EventFormat[];
  relationships: RelationshipFilter[];
  timesOfDay: TimeOfDayFilter[];
  vibes: string[];
  hiddenEntityIds: number[];
}

export const EMPTY_FILTERS: EventFilterState = {
  horizonDays: DEFAULT_UPCOMING_HORIZON_DAYS,
  dateFrom: '',
  dateTo: '',
  formats: [],
  relationships: [],
  timesOfDay: [],
  vibes: [],
  hiddenEntityIds: [],
};

export const FORMAT_OPTIONS: Array<{ value: EventFormat; label: string }> = [
  { value: 'in-person', label: 'In-person' },
  { value: 'virtual', label: 'Virtual' },
  { value: 'hybrid', label: 'Hybrid' },
];

export const RELATIONSHIP_OPTIONS: Array<{ value: RelationshipFilter; label: string }> = [
  { value: 'HOSTING', label: 'Hosting' },
  { value: 'SPONSORING', label: 'Sponsor' },
  { value: 'ATTENDING', label: 'Attending' },
];

export const TIME_OF_DAY_OPTIONS: TimeOfDayFilter[] = ['Morning', 'Afternoon', 'Evening'];

const RELATIONSHIP_ALIASES: Record<string, RelationshipFilter> = {
  HOSTING: 'HOSTING',
  SPONSORING: 'SPONSORING',
  SPONSOR: 'SPONSORING',
  ATTENDING: 'ATTENDING',
};

/** Number of active filter groups, shown next to the Filters control. */
export function countActiveFilters(filters: EventFilterState): number {
  return (
    (filters.dateFrom || filters.dateTo || filters.horizonDays !== DEFAULT_UPCOMING_HORIZON_DAYS ? 1 : 0) +
    filters.formats.length +
    filters.relationships.length +
    filters.timesOfDay.length +
    filters.vibes.length +
    filters.hiddenEntityIds.length
  );
}

export function hasActiveFilters(filters: EventFilterState): boolean {
  return countActiveFilters(filters) > 0;
}

/** Every vibe present in the current result set, for the vibe picker. */
export function collectVibeOptions(events: Event[]): string[] {
  const seen = new Set<string>();
  for (const event of events || []) {
    for (const vibe of getVibes(event)) seen.add(vibe);
  }
  return Array.from(seen).sort((a, b) => a.localeCompare(b));
}

function matchesDateRange(event: Event, filters: EventFilterState): boolean {
  if (filters.dateFrom) {
    // An event still qualifies if it is running across the lower bound.
    const endComparison = compareEventLocalDates(event.endDate || event.startDate, filters.dateFrom);
    if (endComparison != null && endComparison < 0) return false;
  }
  const dateTo = filters.dateTo || localCalendarDateOffset(filters.horizonDays);
  if (dateTo) {
    const startComparison = compareEventLocalDates(event.startDate, dateTo);
    if (startComparison != null && startComparison > 0) return false;
  }
  return true;
}

function matchesRelationship(event: Event, filters: EventFilterState): boolean {
  if (filters.relationships.length === 0) return true;
  const raw = (event.entityRelationship || '').toUpperCase();
  const normalized = RELATIONSHIP_ALIASES[raw];
  return normalized != null && filters.relationships.includes(normalized);
}

/** Apply every active filter. Groups are AND-ed; values within a group are OR-ed. */
export function applyEventFilters(events: Event[], filters: EventFilterState): Event[] {
  if (!Array.isArray(events)) return [];

  return events.filter((event) => {
    if (!matchesDateRange(event, filters)) return false;
    if (filters.formats.length > 0 && !filters.formats.includes(getEventFormat(event))) return false;
    if (!matchesRelationship(event, filters)) return false;
    if (filters.timesOfDay.length > 0) {
      const timeOfDay = getTimeOfDay(event) as TimeOfDayFilter;
      if (!timeOfDay || !filters.timesOfDay.includes(timeOfDay)) return false;
    }
    if (filters.vibes.length > 0) {
      const vibes = getVibes(event);
      if (!vibes.some((vibe) => filters.vibes.includes(vibe))) return false;
    }
    if (filters.hiddenEntityIds.length > 0 && Array.isArray(event.entities) && event.entities.length > 0) {
      if (event.entities.every((entity) => filters.hiddenEntityIds.includes(entity.id))) return false;
    }
    return true;
  });
}

// ---------------------------------------------------------------------------
// URL serialization
// ---------------------------------------------------------------------------

const PARAM_KEYS = {
  horizonDays: 'days',
  dateFrom: 'from',
  dateTo: 'to',
  formats: 'format',
  relationships: 'rel',
  timesOfDay: 'tod',
  vibes: 'vibe',
  hiddenEntityIds: 'hideEntity',
} as const;

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function readList(params: URLSearchParams, key: string): string[] {
  const raw = params.get(key);
  if (!raw) return [];
  return raw.split(',').map((v) => v.trim()).filter(Boolean);
}

/** Parse filters out of the URL, ignoring anything unrecognised. */
export function parseFiltersFromParams(params: URLSearchParams): EventFilterState {
  const parsedHorizonDays = Number(params.get(PARAM_KEYS.horizonDays));
  const dateFrom = params.get(PARAM_KEYS.dateFrom) || '';
  const dateTo = params.get(PARAM_KEYS.dateTo) || '';
  const validFormats = new Set(FORMAT_OPTIONS.map((o) => o.value as string));
  const validRelationships = new Set(RELATIONSHIP_OPTIONS.map((o) => o.value as string));
  const validTimes = new Set<string>(TIME_OF_DAY_OPTIONS);

  return {
    horizonDays: UPCOMING_HORIZON_OPTIONS.includes(parsedHorizonDays as UpcomingHorizonDays)
      ? parsedHorizonDays as UpcomingHorizonDays
      : DEFAULT_UPCOMING_HORIZON_DAYS,
    dateFrom: DATE_PATTERN.test(dateFrom) ? dateFrom : '',
    dateTo: DATE_PATTERN.test(dateTo) ? dateTo : '',
    formats: readList(params, PARAM_KEYS.formats).filter((v) => validFormats.has(v)) as EventFormat[],
    relationships: readList(params, PARAM_KEYS.relationships)
      .map((v) => v.toUpperCase())
      .filter((v) => validRelationships.has(v)) as RelationshipFilter[],
    timesOfDay: readList(params, PARAM_KEYS.timesOfDay).filter((v) => validTimes.has(v)) as TimeOfDayFilter[],
    vibes: readList(params, PARAM_KEYS.vibes),
    hiddenEntityIds: readList(params, PARAM_KEYS.hiddenEntityIds)
      .map(Number)
      .filter((value) => Number.isInteger(value) && value > 0),
  };
}

export function getEffectiveDateTo(filters: EventFilterState): string {
  return filters.dateTo || localCalendarDateOffset(filters.horizonDays);
}

/** Write filters into an existing param set, removing empty groups. */
export function writeFiltersToParams(params: URLSearchParams, filters: EventFilterState): URLSearchParams {
  const next = new URLSearchParams(params);

  const setOrDelete = (key: string, value: string) => {
    if (value) next.set(key, value);
    else next.delete(key);
  };

  setOrDelete(
    PARAM_KEYS.horizonDays,
    filters.horizonDays === DEFAULT_UPCOMING_HORIZON_DAYS ? '' : String(filters.horizonDays),
  );
  setOrDelete(PARAM_KEYS.dateFrom, filters.dateFrom);
  setOrDelete(PARAM_KEYS.dateTo, filters.dateTo);
  setOrDelete(PARAM_KEYS.formats, filters.formats.join(','));
  setOrDelete(PARAM_KEYS.relationships, filters.relationships.join(','));
  setOrDelete(PARAM_KEYS.timesOfDay, filters.timesOfDay.join(','));
  setOrDelete(PARAM_KEYS.vibes, filters.vibes.join(','));
  setOrDelete(PARAM_KEYS.hiddenEntityIds, filters.hiddenEntityIds.join(','));

  return next;
}

/** Toggle one value inside a multi-select filter group. */
export function toggleFilterValue<T extends string>(current: T[], value: T): T[] {
  return current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
}
